import React from "react";
import { motion } from "framer-motion";
import { Tooltip, Tag } from "antd";
import { ListMusic, Film, ChevronRight, GitBranch } from "lucide-react";
import Animations from "../../animations/Animations";
import ActionButton from "../ActionButton";
import PermissionManager from "../../managers/PermissionManager";
import EmptyState from "../EmptyState";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../global/ProLanguageContext";

const PlaylistTableView = ({
  data,
  onRowClicked = () => {},
  onDelete = () => {},
  onEdit = () => {},
  onDuplicate = () => {},
  branches = [],
  selectedBrandData,
  selectedBranchData,
}) => {
  const { lang } = useProLanguage();
  const t = PRO_TRANSLATIONS[lang].playlist;

  const branchMap = {};
  branches.forEach((b) => {
    if (b.BranchID)
      branchMap[b.BranchID] = b.BranchName || `Branch ${b.BranchID}`;
  });

  const editPermission = PermissionManager.checkPermission(
    PermissionManager.PERMISSION_KEY_PLAYLIST,
    PermissionManager.PERMISSION_TYPE_EDIT,
  );
  const deletePermission = PermissionManager.checkPermission(
    PermissionManager.PERMISSION_KEY_PLAYLIST,
    PermissionManager.PERMISSION_TYPE_DELETE,
  );

  const sharingMode = selectedBrandData
    ? selectedBrandData.ContentSharingMode
    : null;
  const isBrandLevel = !selectedBranchData;
  const showOwnershipAndBranches =
    (sharingMode == 2 || sharingMode == "2") && isBrandLevel;

  if (data.length === 0) {
    return (
      <EmptyState
        icon={<ListMusic size={28} color="#6366f1" />}
        title="No playlists found"
        description="Create your first playlist to start scheduling content."
      />
    );
  }

  return (
    <motion.div
      variants={Animations.LIST_CONTAINER}
      initial="hidden"
      animate="show"
      key={data.length > 0 ? "loaded" : "empty"}
    >
      {data.map((record, index) => {
        const mediaCount = record.Medias ? record.Medias.length : 0;
        const isBrandOwned =
          record.CreateLevel === 1 || record.CreateLevel === "1";
        const raw = record.AccessBranches;
        const isAll = !raw || raw === "null" || raw === "all" || raw === "";
        const branchIds = isAll
          ? []
          : String(raw)
              .split(",")
              .map((s) => s.trim())
              .filter((id) => id !== "");

        return (
          <motion.div
            key={record.PlaylistID || index}
            variants={Animations.LIST_ITEM}
            onClick={() => onRowClicked(record)}
            style={{
              border: "1px solid #f1f5f9",
              borderRadius: 18,
              marginBottom: 12,
              backgroundColor: "#fff",
              display: "flex",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              transition: "box-shadow 0.2s ease",
              cursor: "pointer",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.boxShadow =
                "0 6px 20px rgba(99,102,241,0.10)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)")
            }
          >
            {/* Accent bar */}
            <div
              style={{
                width: 4,
                alignSelf: "stretch",
                flexShrink: 0,
                background: "linear-gradient(180deg, #818cf8, #6366f1)",
              }}
            />

            {/* Icon */}
            <div style={{ padding: "14px 0 14px 16px", flexShrink: 0 }}>
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 14,
                  flexShrink: 0,
                  background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ListMusic size={22} color="#6366f1" />
              </div>
            </div>

            {/* Main info */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRowClicked(record);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 15,
                    color: "#0f172a",
                    textAlign: "left",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                    marginBottom: 8,
                    transition: "color 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "#6366f1")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "#0f172a")
                  }
                >
                  {record.PlaylistName}
                </button>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  {/* Media count pill */}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      padding: "3px 10px",
                      borderRadius: 20,
                      background: "linear-gradient(135deg, #faf5ff, #ede9fe)",
                      border: "1px solid #ddd6fe",
                      fontSize: 12,
                      fontWeight: 500,
                      color: "#7c3aed",
                    }}
                  >
                    <Film size={11} strokeWidth={2.2} />
                    {mediaCount} {mediaCount === 1 ? "media" : "medias"}
                  </span>
                  {showOwnershipAndBranches && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "3px 10px",
                        borderRadius: 20,
                        background: isBrandOwned
                          ? "linear-gradient(135deg,#fffbeb,#fef3c7)"
                          : "linear-gradient(135deg,#eff6ff,#dbeafe)",
                        border: `1px solid ${isBrandOwned ? "#fde68a" : "#bfdbfe"}`,
                        fontSize: 12,
                        fontWeight: 500,
                        color: isBrandOwned ? "#b45309" : "#2563eb",
                      }}
                    >
                      <GitBranch size={11} strokeWidth={2.2} />
                      {isBrandOwned ? "Workspace" : "Location"}
                    </span>
                  )}
                  {showOwnershipAndBranches &&
                    (isAll || branchIds.length === 0 ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "3px 10px",
                          borderRadius: 20,
                          background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
                          border: "1px solid #86efac",
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#16a34a",
                        }}
                      >
                        All Branches
                      </span>
                    ) : (
                      branchIds.map((id) => (
                        <span
                          key={id}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "3px 10px",
                            borderRadius: 20,
                            background:
                              "linear-gradient(135deg,#eff6ff,#dbeafe)",
                            border: "1px solid #bfdbfe",
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#2563eb",
                          }}
                        >
                          {branchMap[id] || `Branch ${id}`}
                        </span>
                      ))
                    ))}
                </div>
              </div>

              {/* Actions */}
              {(editPermission || deletePermission) && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    flexShrink: 0,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ActionButton
                    visible={editPermission}
                    onClicked={() => onEdit(record)}
                    title={t.tooltipEdit}
                    showTitle={false}
                    icon="/cms/assets/svgs/icons/edit_icon.svg"
                  />
                  <ActionButton
                    visible={editPermission}
                    onClicked={() => onDuplicate(record)}
                    title={t.tooltipDuplicate}
                    showTitle={false}
                    icon="/cms/assets/svgs/icons/duplicate_icon.svg"
                  />
                  <ActionButton
                    visible={deletePermission}
                    onClicked={() => onDelete(record)}
                    title={t.tooltipDelete}
                    showTitle={false}
                    icon="/cms/assets/svgs/icons/trash_icon.svg"
                  />
                </div>
              )}

              {/* Chevron */}
              <div style={{ color: "#cbd5e1", flexShrink: 0 }}>
                <ChevronRight size={18} strokeWidth={2} />
              </div>
            </div>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default PlaylistTableView;
